import { memo, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import RCCheckbox from 'rc-checkbox';
import { areEqualFields, scrollToNextMatchingSibling } from 'utils/form';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import isRecord from 'utils/isRecord';
import 'rc-checkbox/assets/index.css';
import type { TFieldComponentProps } from './types';
import s from './Checkbox.module.scss';

export interface ICheckboxProps extends TFieldComponentProps<boolean | null | undefined> {
  icon?: ReactNode;
  color?: string;
  skipScrollingToField?: boolean;
  sideEffectOnChange?: (checked: boolean) => void;
  skipScrollingOnChange?: boolean;
  label?: ReactNode;
  className?: string;
  description?: ReactNode;
}

// rc-checkbox calls onChange with `{ target: { ...props, checked } }`; its typings say Event.
const checkedOf = (e: Event) => {
  const { target } = e;
  return isRecord(target) && target.checked === true;
};

const Checkbox = ({
  field: { name, value, onBlur },
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  icon = null,
  color = 'black',
  skipScrollingToField,
  sideEffectOnChange,
  skipScrollingOnChange,
  ...props
}: ICheckboxProps) => {
  const { label, className, description } = props;
  const fieldWrapperRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (e: Event) => {
      const v = checkedOf(e);
      setFieldValue(name, v);
      if (typeof sideEffectOnChange === 'function') sideEffectOnChange(v);
      if (v && !skipScrollingOnChange) scrollToNextMatchingSibling(fieldWrapperRef);
    },
    [name, setFieldValue, sideEffectOnChange, skipScrollingOnChange],
  );

  useEffect(() => {
    if (isNil(value)) setFieldValue(name, false);
  }, [name, setFieldValue, value]);

  const fieldWrapperClassnames = useMemo(() => {
    let classes = '';
    if (!skipScrollingToField) {
      classes += 'scroll-to-field';
    }
    return cn(s.checkboxWrapper, className, classes);
  }, [skipScrollingToField, className]);

  return (
    <div className={fieldWrapperClassnames} ref={fieldWrapperRef}>
      <div className={s.checkboxContent}>
        <RCCheckbox
          name={name}
          id={name}
          type="checkbox"
          value={value || false}
          // rc-checkbox reads a null `checked` like undefined (unchecked, controlled).
          checked={value ?? undefined}
          onChange={handleChange}
          onBlur={onBlur}
          className={s.checkboxInput}
        />
        <label className={s.label} htmlFor={name}>
          <div style={{ color }}>{label}</div>
          {icon ? <div className={s.icon}>{icon}</div> : null}
        </label>
      </div>

      {description ? (
        <div className={s.note}>
          <small className="muted">{description}</small>
        </div>
      ) : null}
      {errors[name] && touched[name] && <div className="error-text">{errors[name]}</div>}
    </div>
  );
};

export default memo(Checkbox, areEqualFields);
